import type { GetServerSideProps } from "next";
import { toInteger } from "lodash";
import {
  IWordpressCategory,
  getCategories,
  getPosts,
  getRecentListingsForBlog,
  isInvalidPageError,
} from "../../../../components/wordpress/api";
import Blog, { IBlogProps } from "../../../../components/wordpress/Blog";
import absoluteUrl from "next-absolute-url";
import { BreadcrumbJsonLd, CollectionPageJsonLd, NextSeo } from "next-seo";
import { removeHtmlTags } from "../../../../lib/utils";
import axios from "axios";
import { initPath } from "../../../../lib/api/Api";

function redirectToPage(url: string, permanent = false) {
  return {
    redirect: {
      permanent,
      destination: url,
    },
  };
}

export const getServerSideProps = (async (context) => {
  const { origin } = absoluteUrl(context.req);
  const slug = context.params?.slug ?? "";

  let page = context.query.page ? toInteger(context.query.page) : 1;
  page = page < 1 ? 1 : page;

  try {
    const categories = await getCategories();
    const category = categories.data.find((c) => c.slug === slug);

    if (category === undefined) {
      return redirectToPage("/blog");
    }

    const initDataPromise = await axios({
      method: "GET",
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
      },
      url: `${origin}${initPath}`,
    });

    const [posts, recentListings, initDataResponse] = await Promise.all([
      getPosts({ page, category: category.id }),
      getRecentListingsForBlog(origin),
      initDataPromise,
    ]);

    return {
      props: {
        currentCategory: category,
        posts: posts.data,
        page,
        totalPages: toInteger(posts.headers["x-wp-totalpages"]),
        categories: categories.data,
        recentListings,
        initData: initDataResponse.data,
      },
    };
  } catch (error) {
    if (isInvalidPageError(error)) {
      return redirectToPage("/blog");
    }

    throw error;
  }
}) satisfies GetServerSideProps<IBlogProps>;

function BlogPage(props: IBlogProps & { currentCategory: IWordpressCategory }) {
  const postCategoryUrl = `${props.initData.host}/blog/category/${props.currentCategory.slug}/`;
  const title = `${props.currentCategory.name} Category | Blog | Purr`;
  const description = removeHtmlTags(props.currentCategory?.description ?? "");
  return (
    <>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          title: title,
          description: description,
          type: "website",
          site_name: "Purr",
          locale: "en-US",
        }}
      />

      <CollectionPageJsonLd
        name={title}
        description={description}
        inLanguage="en-US"
        url={postCategoryUrl}
        hasPart={props.posts.map((post: any) => {
          return {
            name: post.title.rendered,
            about: removeHtmlTags(post.excerpt.rendered),
            author: post.author_info?.display_name ?? "Purr",
            datePublished: post.date,
            audience: "Internet",
            thumbnailUrl: post.featured_image_src,
          };
        })}
      />

      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: "Blog",
            item: `${props.initData.host}/blog`,
          },
          {
            position: 2,
            name: props.currentCategory.name,
            item: postCategoryUrl,
          },
        ]}
      />
      <Blog {...props} />
    </>
  );
}

export default BlogPage;
